import { computed, readonly, type MaybeRefOrGetter, type Ref } from 'vue';
import type { ApiCandidateDetailLobbyist } from '~/composables/useCandidate';
import { useCandidateDetail } from '~/composables/useCandidate';
import type { Lobbyist } from '~/types/Candidates';
import type { ApiError } from '~/types/plugins/api';
import { normalizeExternalUrl } from '~/utils/normalizeExternalUrl';

function mapLobbyist(raw: ApiCandidateDetailLobbyist): Lobbyist {
  return {
    id: raw.slug,
    name: raw.name,
    industry: raw.industry ?? '',
    description: raw.description?.trim() || null,
    url: normalizeExternalUrl(raw.website),
    logo: raw.logo?.thumb_url ?? raw.logo?.url ?? null,
    candidateIds: [],
  };
}

// No dedicated `/candidates/{id}/lobbyists` endpoint exists — the candidate's
// lobbyists are only available via the Show Candidate response, so this reads
// them off `useCandidateDetail`, which is the single fetch shared with
// useCandidate/useCandidateCampaigns for the same id (see the dedup note on
// `useCandidateDetail` in useCandidate.ts).
export function useCandidateLobbyists(id: MaybeRefOrGetter<string>) {
  const { data, error, status } = useCandidateDetail(id);

  const lobbyists = computed(
    () => data.value?.lobbyists?.map(mapLobbyist) ?? [],
  );

  return {
    lobbyists: readonly(lobbyists) as Readonly<Ref<Lobbyist[]>>,
    pending: computed(() => status.value === 'pending'),
    error: readonly(error) as Readonly<Ref<ApiError | null>>,
  };
}
